import gql from 'graphql-tag';

export const typeDefs = gql`
  interface CMSItemCustomFieldAccessors {
    """
    Accessor for Boolean fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldBool(id: ID, slug: String): Boolean @pickOne

    """
    Accessor for Color fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldColor(id: ID, slug: String): String @pickOne

    """
    Accessor for E-commerce Price fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldCommercePrice(id: ID, slug: String): CMSPrice @pickOne

    """
    Accessor for E-commerce SKU Property Table fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldCommercePropTable(id: ID, slug: String): [CMSCommerceSKUProperty]
      @pickOne

    """
    Accessor for E-commerce SKU Property Value fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldCommercePropValues(id: ID, slug: String): [CMSCommerceSKUValue]
      @pickOne

    """
    Accessor for Date fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldDate(id: ID, slug: String): Date @pickOne

    """
    Accessor for DateTime fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldDateTime(id: ID, slug: String): DateTime @pickOne

    """
    Accessor for Email fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldEmail(id: ID, slug: String): String @pickOne

    """
    Accessor for Enum Option fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldEnumOption(id: ID, slug: String): String @pickOne

    """
    Accessor for External File Reference fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldExtFileRef(id: ID, slug: String): CMSExtFileRefField @pickOne

    """
    Accessor for File Reference fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldFileRef(id: ID, slug: String): CMSFileRefField @pickOne

    # """
    # Accessor for Image fields.

    # Works for both field ids (32-char hex) and field slugs.
    # """
    # fieldImage(id: ID, slug: String): ??? @pickOne

    """
    Accessor for Image Reference fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldImageRef(id: ID, slug: String): CMSImageRefField @pickOne

    """
    Accessor for ItemRef fields.

    This only returns the Id data, it doesn't provide an API to lookup the remote item.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldItemRef(id: ID, slug: String): ID @pickOne

    """
    Accessor for ItemRefSet fields.

    This only returns the Id data, it doesn't provide an API to lookup the remote item.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldItemRefSet(id: ID, slug: String): [ID] @pickOne

    """
    Accessor for Link fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldLink(id: ID, slug: String): String @pickOne

    """
    Accessor for Number fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldNumber(id: ID, slug: String): Float @pickOne

    """
    Accessor for Option fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldOption(id: ID, slug: String): CMSOptionField @pickOne

    """
    Accessor for Phone fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldPhone(id: ID, slug: String): String @pickOne

    """
    Accessor for PlainText fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldPlainText(id: ID, slug: String): String @pickOne

    """
    Accessor for RichText fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldRichText(id: ID, slug: String): RichText @pickOne

    """
    Accessor for Sets (lists) of ExtFileRef fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldSetOfExtFileRef(id: ID, slug: String): [CMSExtFileRefField] @pickOne

    """
    Accessor for Sets (lists) of ImageRef fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldSetOfImageRef(id: ID, slug: String): [CMSImageRefField] @pickOne

    """
    Accessor for Subscription Plan fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldSubscriptionPlan(id: ID, slug: String): CMSCommerceSubscriptionPlan
      @pickOne

    """
    Accessor for User fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldUser(id: ID, slug: String): String @pickOne

    """
    Accessor for Video fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldVideo(id: ID, slug: String): CMSVideoField @pickOne

    """
    Accessor for hydrating the ItemRef.

    Works for both field ids (32-char hex) and field slugs.
    """
    resolveLocalItemRef(id: ID, slug: String): CMSItem @pickOne

    """
    Accessor for hydrating the ItemRefSet.

    Works for both field ids (32-char hex) and field slugs.
    """
    resolveLocalItemRefSet(id: ID, slug: String): [CMSItem] @pickOne
  }

  type Query {
    """
    Our CMS feature.

    CMS data can be stored in a number of environments. Every site gets precisely 1 staging environment. (It's what is
    rendered in the designer.) And every time a site gets published, a snapshot of the CMS data is taken, and stored in
    an environment keyed by a publication id.
    """
    cms(
      """
      Select the "staging" environment, which is the work-in-progress version of a Site's CMS, which is viewed and
      managed in the designer and data manager.
      """
      staging: Boolean

      """
      Select a "published" environment, which is a point-in-time snapshot of a Site's CMS, taken when users press the
      "publish" button.

      Published environments are identified by a "publication" id, which is a 24-char hex string.
      """
      publication: ID
    ): CMSNamespace @pickOne(args: ["staging", "publication"])
  }

  """
  A single place for all CMS functionality.
  """
  type CMSNamespace {
    """
    A string that defines where CMS data gets stored, and what logic is used to access it.
    """
    storageLayerConfig: String!

    """
    Accessor for a CMS Collection, which is where all user items are stored.

    Can be accessed by id (24-char hex) or slug, but not both.
    """
    collection(id: ID, slug: String): CMSCollection @pickOne

    """
    Produce a summary of all collections within a Site CMS.

    Returns a CMSCollectionInfo, that gives fields to describe a collection, but will not give access to CMS Item
    accessors.
    """
    collections: [CMSCollectionInfo]
  }

  """
  A single CMS Collection.

  A Collection defines a schema for CMS Items, and also provides methods to fetch items attached to that schema.
  """
  type CMSCollection {
    """
    Primary key for the Collection.
    """
    id: ID!
    """
    Name of the collection. Typically plural, like "Blog Posts" or "Authors".
    """
    name: String!
    """
    Collection slug.
    """
    slug: String!
    """
    A singular name for the collection. Used in certain interfaces.
    """
    singularName: String
    """
    The schema that all Items within this collection can have.
    """
    fields: [CMSCollectionField]

    """
    Accessor for singular CMS Items.

    An item can be looked up by either its id (24-char hex) or by its slug field, but not by both.
    """
    item(id: ID, slug: String, locale: ID): CMSItem
      @pickOne(args: ["id", "slug"])
  }

  """
  A single CMS Collection.

  A Collection defines a schema for CMS Items, and also provides methods to fetch items attached to that schema.
  """
  type CMSCollectionInfo {
    """
    Primary key for the Collection.
    """
    id: ID!

    """
    Name of the collection. Typically plural, like "Blog Posts" or "Authors".
    """
    name: String!

    """
    Collection slug.
    """
    slug: String!

    """
    A singular name for the collection. Used in certain interfaces.
    """
    singularName: String

    """
    The schema that all Items within this collection can have.
    """
    fields: [CMSCollectionField]
  }

  type CMSCollectionField {
    """
    The field id. A 32-character hex value. (Not a typo - this is *NOT* a Mongo id.)
    """
    id: ID!

    """
    The user-defined name for this field.
    """
    name: String!

    """
    A string derived from the name field. Is used as the key for this field in the Public API and bindings.

    For historical reasons, this slug is unique over the _lifetime_ of the collection. If a user creates a "Blah" field,
    the slug would be "blah". But if that field gets removed, and then another "Blah" field is created, the second field
    will have a slug of "blah-2".
    """
    slug: String!

    """
    Whether this field is required.

    Note: This field doesn't mean the field is non-nullable. There are a few circumstances that can lead to this field
    having a null value:

    1. If the Item is marked as "draft", then we don't validate nullability.
    2. If a required field is introduced or retroactively marked as required, pre-existing items might still have null
       values stored.
    """
    required: Boolean!

    """
    The 'type' of this field. Defines the values that can be stored, the queries that can be performed, and whether the
    field can be used in sorts.

    Is an enum defined by the DYNAMO_TYPES array in @packages/systems/dynamo/config. Is only not an enum in GraphQL
    because it doesn't NEED to be, and it would add extra complexity when new types get introduced.
    """
    type: String!

    """
    The nested type for this field. This field should only ever be set when type is "Set".
    """
    innerType: String
  }

  """
  A single Item within the CMS. Contains customer-defined data, in the shape of the attached Collection.
  """
  type CMSItem implements CMSItemCustomFieldAccessors {
    """
    Primary key for a logical CMS Item.

    Technically, this value isn't fully unique within a database, since a single logical CMS Item could have multiple
    locales.
    """
    id: ID!

    """
    "Name" field for the CMS Item.
    """
    name: String!

    """
    "Slug" field for the CMS Item.
    """
    slug: String!

    """
    The path to the item detail page, e.g. "/en/parent-folder/authors/jane-austen"
    """
    fullSlug: String

    """
    True when this Item is in draft mode.

    Items in draft mode will only be visible in Staged environments.
    """
    draft: Boolean!

    """
    True when this Item is in archived mode.

    Items in archived mode will only be visible in Staged environments.
    """
    archived: Boolean!

    """
    The timestamp for when this CMS Item was initially created.
    """
    createdOn: DateTime

    """
    The timestamp for when this CMS Item was last updated.
    """
    updatedOn: DateTime

    """
    The timestamp for when changes to this CMS Item was last published.
    """
    publishedOn: DateTime

    """
    A string describing who created this CMS Item.

    Not particularly useful for end-users.
    """
    createdBy: String

    """
    A string describing who last updated this CMS Item.

    Not particularly useful for end-users.
    """
    updatedBy: String

    """
    A string describing who last published changes to this CMS Item.

    Not particularly useful for end-users.
    """
    publishedBy: String

    """
    The id of the CMS Locale that this Item is in.
    """
    cmsLocaleId: ID

    """
    Boolean to toggle site-indexing for the Item.
    """
    noSearch: Boolean!

    """
    Accessor for Boolean fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldBool(id: ID, slug: String): Boolean @pickOne

    """
    Accessor for Color fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldColor(id: ID, slug: String): String @pickOne

    """
    Accessor for E-commerce Inventory Quantity fields.
    """
    fieldCommerceInventoryQuantity: Int

    """
    Accessor for E-commerce Price fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldCommercePrice(id: ID, slug: String): CMSPrice @pickOne

    """
    Accessor for E-commerce SKU Property Table fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldCommercePropTable(id: ID, slug: String): [CMSCommerceSKUProperty]
      @pickOne

    """
    Accessor for E-commerce SKU Property Value fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldCommercePropValues(id: ID, slug: String): [CMSCommerceSKUValue]
      @pickOne

    """
    Accessor for Date fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldDate(id: ID, slug: String): Date @pickOne

    """
    Accessor for DateTime fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldDateTime(id: ID, slug: String): DateTime @pickOne

    """
    Accessor for Email fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldEmail(id: ID, slug: String): String @pickOne

    """
    Accessor for Enum Option fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldEnumOption(id: ID, slug: String): String @pickOne

    """
    Accessor for External File Reference fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldExtFileRef(id: ID, slug: String): CMSExtFileRefField @pickOne

    """
    Accessor for File Reference fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldFileRef(id: ID, slug: String): CMSFileRefField @pickOne

    # """
    # Accessor for Image fields.

    # Works for both field ids (32-char hex) and field slugs.
    # """
    # fieldImage(id: ID, slug: String): ??? @pickOne

    """
    Accessor for Image Reference fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldImageRef(id: ID, slug: String): CMSImageRefField @pickOne

    """
    Accessor for ItemRef fields.

    This only returns the Id data, it doesn't provide an API to lookup the remote item.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldItemRef(id: ID, slug: String): ID @pickOne

    """
    Accessor for ItemRefSet fields.

    This only returns the Id data, it doesn't provide an API to lookup the remote item.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldItemRefSet(id: ID, slug: String): [ID] @pickOne

    """
    Accessor for Link fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldLink(id: ID, slug: String): String @pickOne

    """
    Accessor for Number fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldNumber(id: ID, slug: String): Float @pickOne

    """
    Accessor for Option fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldOption(id: ID, slug: String): CMSOptionField @pickOne

    """
    Accessor for Phone fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldPhone(id: ID, slug: String): String @pickOne

    """
    Accessor for PlainText fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldPlainText(id: ID, slug: String): String @pickOne

    """
    Accessor for RichText fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldRichText(id: ID, slug: String): RichText @pickOne

    """
    Accessor for Sets (lists) of ExtFileRef fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldSetOfExtFileRef(id: ID, slug: String): [CMSExtFileRefField] @pickOne

    """
    Accessor for Sets (lists) of ImageRef fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldSetOfImageRef(id: ID, slug: String): [CMSImageRefField] @pickOne

    """
    Accessor for Subscription Plan fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldSubscriptionPlan(id: ID, slug: String): CMSCommerceSubscriptionPlan
      @pickOne

    """
    Accessor for User fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldUser(id: ID, slug: String): String @pickOne

    """
    Accessor for Video fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldVideo(id: ID, slug: String): CMSVideoField @pickOne

    """
    Accessor for hydrating the ItemRef.

    Works for both field ids (32-char hex) and field slugs.
    """
    resolveLocalItemRef(id: ID, slug: String): CMSItem @pickOne

    """
    Accessor for hydrating the ItemRefSet.

    Works for both field ids (32-char hex) and field slugs.
    """
    resolveLocalItemRefSet(id: ID, slug: String): [CMSItem] @pickOne
  }

  """
  An ImageRef value, stored in the CMS.
  """
  type CMSImageRefField {
    """
    URL for this asset. Should be ready-to-use. (ie: the CDN URL.)
    """
    url: String
    """
    Information on the file this ImageRef is pointing to.
    """
    file: CMSImageFileInfo

    """
    Alt text for the asset.
    """
    alt: String
  }

  """
  Information on a single image file asset, stored in our CMS.
  """
  type CMSImageFileInfo {
    """
    Size of the asset, in bytes.
    """
    size: Int

    """
    Original name of the asset, before URI encoding / file id prefixing / etc.
    """
    origFileName: String

    """
    Timestamp when this asset was created.
    """
    createdOn: DateTime

    """
    Timestamp when this asset was last updated.
    """
    updatedOn: DateTime

    """
    MIME type for this asset. Like "image/jpeg".
    """
    mimeType: String

    """
    Width of this asset, in pixels.
    """
    width: Float

    """
    Height of this asset, in pixels.
    """
    height: Float

    """
    Variants of this asset that have been stored.

    Context: Some assets might get transcoded into other versions. This could be smaller versions that'd be
    conditionally used in some circumstances.
    """
    variants: [CMSImageFileVariant]
  }

  """
  Information on an image asset's variant.
  """
  type CMSImageFileVariant {
    """
    Original file name of the variant.
    """
    origFileName: String

    """
    Quality setting used when producing this variant. Values can range from 0 to 100.
    """
    quality: Float

    """
    Height of the variant, in pixels.

    Might be null, if the height wasn't a consideration in the scaling.
    """
    height: Int

    """
    Width of the variant, in pixels.

    Might be null, if the width wasn't a consideration in the scaling.
    """
    width: Int

    """
    Path to the asset in S3. (NOT the CDN.)
    """
    s3Url: String

    """
    If there was an error transcoding this variant, an error message for that failure. Otherwise, null.
    """
    error: String

    """
    Size of the variant, in bytes.
    """
    size: Int
  }

  """
  An Option value, stored in the CMS.
  """
  type CMSOptionField {
    """
    Id value for this field. A 32-char hex string. (NOT a mongo id.)
    """
    id: ID!

    """
    The user-defined name associated with the id value.
    """
    name: String
  }

  """
  A video reference, stored in the CMS.
  """
  type CMSVideoField {
    """
    The video url. Usually stored in vimeo or youtube or similar.
    """
    url: String
    metadata: CMSVideoFieldMetadata
  }

  """
  Additional video details, stored alongside the main url.
  """
  type CMSVideoFieldMetadata {
    html: String
    height: Float
    width: Float
    # Note: snake_case here simply because it's what the storage layer returns
    thumbnail_url: String
  }

  """
  A File asset, stored in the CMS.
  """
  type CMSFileRefField {
    """
    Public URL (ie: uses a CDN) to access this file.
    """
    url: String

    """
    Further details on this file.
    """
    file: CMSFileDetails
  }

  """
  File details, my dawg.
  """
  type CMSFileDetails {
    """
    File size, in bytes.
    """
    size: Int

    """
    Original name of the file, without url-encoding, or id prefixes.
    """
    origFileName: String

    """
    Timestamp for when this file was uploaded.
    """
    createdOn: DateTime

    """
    Timestamp for when this file was last updated.
    """
    updatedOn: DateTime

    """
    MIME type for this file. Something like: "application/pdf"
    """
    mimeType: String
  }

  """
  An _externally_-hosted file.
  """
  type CMSExtFileRefField {
    """
    URL for this external file.
    """
    url: String

    """
    The external file's filename.
    """
    name: String
  }

  """
  Some monetary value.
  """
  type CMSPrice {
    """
    The number part of a price. What this value means is determined by the currency, and is similar to how Stripe
    defines its prices.
    """
    value: Float

    """
    The currency code for this price. Will be a 3-character ISO-4217 currency code.
    """
    unit: String

    """
    The number part of the price in decimal notation.

    For example, with "USD" as the currency unit, the value is interpretted as US cents, even though most prices are
    actually in dollars. So a value of 123 in USD would result in a 1.23 decimalValue.
    """
    decimalValue: Float

    """
    The current price, rendered as a string.
    """
    string: String
  }

  """
  A table defining valid SKU properties for a specific Ecommerce Product.
  """
  type CMSCommerceSKUProperty {
    """
    Property id. A 32-char hex string.
    """
    id: ID

    """
    Property name. Used as a display value.
    """
    name: String

    """
    Valid values for this SKU property.
    """
    enum: [CMSCommerceSKUPropertyValue]
  }

  type CMSCommerceSKUPropertyValue {
    """
    Value id. A 32-char hex string.
    """
    id: ID

    """
    Value name. Used as a display value.
    """
    name: String

    """
    Value slug. Used in things like item slugs.
    """
    slug: String
  }

  """
  A single SKU value selection.
  """
  type CMSCommerceSKUValue {
    """
    Property id. Defined in this SKU's product's SKU-properties table.
    """
    property: CMSCommerceSKUValueField

    """
    Value id. Defined in this SKU's product's SKU-properties table.
    """
    value: CMSCommerceSKUValueField
  }

  type CMSCommerceSKUValueField {
    """
    The id of the property or value of this SKU.
    """
    id: String
  }

  """
  An Ecommerce subscription plan.
  """
  type CMSCommerceSubscriptionPlan {
    """
    Unit for billing frequency.
    """
    interval: CMSCommerceSubscriptionInterval

    """
    Frequency of billing, with the interval enum. (So if interval is "weeks" and this is 2, every 2 weeks.)
    """
    frequency: Int

    """
    Trial period, in days.
    """
    trial: Int

    """
    Details on the service providing the billing functionality.
    """
    plans: [CMSCommerceSubscriptionProvider]
  }

  """
  Billing provider details.
  """
  type CMSCommerceSubscriptionProvider {
    """
    Provider primary key.
    """
    id: ID

    """
    The platform in use. (eg: stripe)
    """
    platform: CMSCommerceSubscriptionPlatform

    """
    The billing status. (eg: active)
    """
    status: CMSCommerceSubscriptionStatus
  }

  """
  A billing interval for a Commerce subscription. (eg: month, year, ...)
  """
  enum CMSCommerceSubscriptionInterval {
    day
    week
    month
    year
  }

  """
  Services that can provide billing support.
  """
  enum CMSCommerceSubscriptionPlatform {
    stripe
  }

  """
  Services that can provide billing support.
  """
  enum CMSCommerceSubscriptionStatus {
    active
    inactive
  }
`;
